import React from 'react';
import { ProfileData } from '../../hooks/useC2ProfileData';
import './C2Profile.css';

interface C2ProfileUIProps {
  profileData: ProfileData;
  onBackClick: () => void;
  onDownloadPDF: () => void;
  onPrint: () => void;
  onEmail: () => void;
  onRequestReScreening: () => void;
}

export const C2ProfileUI: React.FC<C2ProfileUIProps> = ({
  profileData,
  onBackClick,
  onDownloadPDF,
  onPrint,
  onEmail,
  onRequestReScreening,
}) => {
  return (
    <main className="portal-content">
      <button className="btn-back" data-testid="back-to-cdd-records-btn" onClick={onBackClick}>
        ← Back to CDD Records
      </button>

      <div className="profile-header">
        <div>
          <h1>{profileData.name}</h1>
          <p>ABN: {profileData.abn} │ ACN: {profileData.acn}</p>
        </div>
        <div className="verification-badge">
          ✅ VERIFIED
        </div>
      </div>

      <div className="profile-section certificate-section">
        <h2>📜 CDD CERTIFICATE</h2>
        <div className="certificate-content">
          <div>
            <p><strong>Certificate ID:</strong> {profileData.certificateId}</p>
            <p><strong>Issued:</strong> {profileData.issued}</p>
          </div>
          <div className="certificate-actions">
            <button className="btn-action" data-testid="download-certificate-btn" onClick={onDownloadPDF}>📥 Download PDF</button>
            <button className="btn-action" data-testid="print-certificate-btn" onClick={onPrint}>🖨️ Print</button>
            <button className="btn-action" data-testid="email-certificate-btn" onClick={onEmail}>📧 Email</button>
          </div>
          <div className="qr-placeholder">
            <div className="qr-code">QR Code</div>
            <p>Scan for verification</p>
          </div>
        </div>
      </div>

      <div className="profile-section verification-section">
        <h2>📋 VERIFICATION RESULTS</h2>
        <div className="verification-grid">
          {Object.entries(profileData.verification).map(([key, value]) => (
            <div key={key} className="verification-item">
              <span className="item-label">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
              <span className={`item-value status-${value.toLowerCase().replace(' ', '-')}`}>
                ✅ {value}
              </span>
            </div>
          ))}
        </div>

        <div className="verification-summary">
          <div className="summary-item">
            <span className="summary-label">Risk Rating ❓:</span>
            <span className="risk-badge low-risk">🟢 {profileData.riskRating}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">ECDD Status:</span>
            <span>{profileData.ecdStatus}</span>
          </div>
        </div>
      </div>

      <div className="profile-section entity-section">
        <h2>📋 ENTITY DETAILS</h2>
        <div className="details-grid">
          <div className="detail-item">
            <label>Company Name:</label>
            <span>{profileData.entityDetails.companyName}</span>
          </div>
          <div className="detail-item">
            <label>ABN:</label>
            <span>{profileData.entityDetails.abn}</span>
          </div>
          <div className="detail-item">
            <label>ACN:</label>
            <span>{profileData.entityDetails.acn}</span>
          </div>
          <div className="detail-item">
            <label>Registration:</label>
            <span>{profileData.entityDetails.registration}</span>
          </div>
          <div className="detail-item">
            <label>Address:</label>
            <span>{profileData.entityDetails.address}</span>
          </div>
        </div>
      </div>

      <div className="profile-section beneficial-owners-section">
        <h2>👥 BENEFICIAL OWNERS (Level 2 - Click to expand)</h2>
        <div className="table-wrapper">
          <table className="owners-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>%</th>
                <th>IDV Status</th>
              </tr>
            </thead>
            <tbody>
              {profileData.beneficialOwners.map((owner, index) => (
                <tr key={index}>
                  <td>{owner.name}</td>
                  <td>{owner.role}</td>
                  <td>{owner.percentage}</td>
                  <td><span className="idv-badge">✅ {owner.idvStatus}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button className="btn-expand" data-testid="expand-level2-details-btn">View Level 2 Details (e.g., Trust structures) →</button>
      </div>

      <div className="profile-section ocdd-section">
        <h2>🤖 OCDD TRACKING</h2>
        <div className="ocdd-details">
          <div className="ocdd-item">
            <label>Last CDD:</label>
            <span>19/01/2025</span>
          </div>
          <div className="ocdd-item">
            <label>Next Review:</label>
            <span>Trigger event based (C1 to advise)</span>
          </div>
        </div>
        <button className="btn-action" data-testid="request-rescreening-btn" onClick={onRequestReScreening}>🔄 Request Re-screening</button>
      </div>
    </main>
  );
};
